// ** React Imports
import { lazy, Suspense, useEffect } from "react"
// ** Router Components
import { HashRouter as AppRouter, Navigate, Route, Routes, useNavigate } from "react-router-dom"
// ** Utils
import { isEmpty, isUserLoggedIn } from "../utils/commonFunction"
// ** Custom Components
import LayoutWrapper from "../components/layout/LayoutWrapper"
import RouteFallback from "../components/ui/RouteFallback/RouteFallback"
// ** Routes & Default Routes
import { DefaultRoute, AcessRoutes } from "./routes"
// ** JWT Decode
import { jwtDecode } from "jwt-decode"
import axios from "axios"
import { useDispatch, useSelector } from "react-redux"
const NotAuthorized = lazy(() => import("../components/CustomComponent/notAuthorised"))
const Error = lazy(() => import("../components/CustomComponent/error"))
const LoginPage = lazy(() => import("../auth/Login"))
const VerifyOTPPage = lazy(() => import("../auth/VerifyOTP"))
const ForgotPasswordPage = lazy(() => import("../auth/ForgotPassword"))
const CardInfo = lazy(() => import("../components/CustomComponent/networkError"))

const Router = () => {
  const isValidToken = useSelector((state) => state?.userSession?.isAcess)
  // NOTE:
  // We must allow public/auth routes (login / OTP / forgot-password) to render even if user isn't authenticated yet.
  // The old logic was forcing every route to /login, which prevented OTP screen from opening.
  const currentHashPath = (window.location.hash || "#/login").replace("#", "")
  const isAuthRoute =
    currentHashPath === "/login" ||
    currentHashPath === "/verify-otp" ||
    currentHashPath === "/forgot-password"

  if (!window.location.href.includes("/#/")) {
    window.location.href = "/#/login"
  }

  if (!isValidToken && !isAuthRoute) {
    localStorage.clear()
    window.location.href = "/#/login"
  }

  const LayoutRoutesAndPaths = (layout) => {
    const LayoutRoutes = []
    const LayoutPaths = []

    if (AcessRoutes) {
      AcessRoutes.filter((route) => {
        if (route.layout === layout || route.layout === undefined) {
          LayoutRoutes.push(route)
          LayoutPaths.push(route.path)
        }
      })
    }

    return { LayoutRoutes, LayoutPaths }
  }
  const FinalRoute = ({ route }) => {
    let action, resource
    const isOnline = navigator.onLine
    if (route.meta) {
      action = route.meta.action || null
      resource = route.meta.resource || null
    }

    if (
      !isUserLoggedIn() &&
      (!route.meta || !route.meta.authRoute) &&
      !route.meta?.publicRoute &&
      isOnline
    ) {
      localStorage.clear()
      return <Navigate to='/login' />
    } else if (!isValidToken) {
      localStorage.clear()
      return <Navigate to='/login' />
    } else if (route.meta?.authRoute && isUserLoggedIn()) {
      return <Navigate to={DefaultRoute} />
    } else {
      return <route.component />
    }
  }

  const ResolveRoutes = () => {
    return AcessRoutes.map((route, index) => {
      const { LayoutRoutes, LayoutPaths } = LayoutRoutesAndPaths(route.layout || "")

      return LayoutRoutes.map((layoutRoute) => (
        <Route
          key={layoutRoute.path}
          path={layoutRoute.path}
          element={
            /*
              Suspense sits INSIDE the layout, not around it. Wrapping the
              whole wrapper unmounted the sidebar and header too, so every
              module switch flashed an empty page while the chunk loaded.
            */
            <LayoutWrapper>
              <Suspense fallback={<RouteFallback />}>
                <FinalRoute route={layoutRoute} />
              </Suspense>
            </LayoutWrapper>
          }
        />
      ))
    })
  }
  return (
    <AppRouter basename={process.env.REACT_APP_BASENAME}>
      <Routes>
        <Route
          path='/'
          element={!isEmpty(isUserLoggedIn()) ? <Navigate to={DefaultRoute} /> : <Navigate to='/login' />}
        />
        <Route
          path='/login'
          element={
            <Suspense fallback={<RouteFallback />}>
              <LoginPage />
            </Suspense>
          }
        />
        <Route
          path='/misc/not-authorized'
          element={
            <Suspense fallback={<RouteFallback />}>
              <NotAuthorized />
            </Suspense>
          }
        />
        <Route
          path='/forgot-password'
          element={
            <Suspense fallback={<RouteFallback />}>
              <ForgotPasswordPage />
            </Suspense>
          }
        />
        <Route
          path='/verify-otp'
          element={
            <Suspense fallback={<RouteFallback />}>
              <VerifyOTPPage />
            </Suspense>
          }
        />
        {ResolveRoutes()}
        <Route
          path='*'
          element={
            <LayoutWrapper>
              <Suspense fallback={<RouteFallback />}>
                <Error />
              </Suspense>
            </LayoutWrapper>
          }
        />
      </Routes>
    </AppRouter>
  )
}

export default Router
